import React, { useEffect, useState } from "react";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import useNumericInput from "../../../hooks/useNumericInput";
import useAlphabeticInput from "../../../hooks/useAlphabeticInput";
import useUsernameInput from "../../../hooks/useUsernameInput";
import StoreAPI from "../../../api/StoreApi";
import AsyncSelect from "react-select/async";
import ImageUploader from "../../../components/partials/ImageUploader";
import StoreAdminAPI from "../../../api/StoreAdminApi";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import SubmitButton from "../../../components/partials/global/SubmitButton";

function AddNew() {
  useNumericInput();
  useAlphabeticInput();
  useUsernameInput();

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [storeList, setStoreList] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const INTIAL_FORM_DATA = {
    name: "",
    phone: "",
    email: "",
    image: "",
    store: "",
    username: "",
    password: "",
  };

  const [formData, setFormData] = useState(INTIAL_FORM_DATA);

  const handleChange = (value, action) => {
    // For normal inputs (event object)
    if (value?.target) {
      const { name, value: val, type, checked } = value.target;

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : val,
      }));
      return;
    }

    const fieldName = action.name;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value ? value.value : "",
    }));
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const loadStores = async (inputValue = "") => {
    const res = await StoreAPI.getListforDropdown({
      search: inputValue,
      limit: 10,
    });
    if (res.code === 200) {
      const options = res.data.map((store) => ({
        label: store.name,
        value: store._id,
      }));

      setStoreList(options);
      return options;
    }
  };

  useEffect(() => {
    loadStores();
  }, []);

  const validate = () => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    const newErrors = {};
    if (!formData.name) {
      newErrors.name = "Name is required";
    }

    if (!formData.store) {
      newErrors.stores = "Store Field is required";
    }

    if (!formData.username) {
      newErrors.username = "Username is required";
    } else if (!usernameRegex.test(formData.username)) {
      return "Username must be 3-20 characters, letters/numbers only";
    }

    if (!formData.email) {
      newErrors.email = "Email Address is required";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be greater than 6 characters";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Invalid Phone Number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    showLoader();
    setIsLoading(true);

    e.preventDefault();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const res = await StoreAdminAPI.addStoreAdmin(formData);

      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INTIAL_FORM_DATA);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Onboard a Store Admin</h4>
            <h6>
              Easily onboard a store administrator to streamline management and
              enhance store performance.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Create New Store Admin</h4>
          </div>
          <div className="card-body profile-body">
            <h5 className="mb-2">
              <i className="ti ti-user text-primary me-1" />
              Basic Information
            </h5>
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="store-admin-profile"
              />

              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Store <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-home"></i>
                      </span>
                      <AsyncSelect
                        cacheOptions
                        name="store"
                        defaultOptions={storeList}
                        loadOptions={loadStores}
                        onChange={handleChange}
                        placeholder="Select Store"
                        isClearable
                      />
                    </div>
                    {errors.selectedStore && (
                      <span className="errors text-danger">
                        {errors.selectedStore}
                      </span>
                    )}
                  </div>
                </div>

                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-user"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control only-alphabets"
                        placeholder="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.name && (
                      <span className="errors text-danger">{errors.name}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        <i className="fa fa-phone"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control only-numeric"
                        placeholder="Mobile Number"
                        maxLength={10}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.phone && (
                      <span className="errors text-danger">{errors.phone}</span>
                    )}
                  </div>
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      User Name<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" id="basic-addon1">
                        @
                      </span>
                      <input
                        type="text"
                        className="form-control only-username"
                        placeholder="Username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.username && (
                      <span className="errors text-danger">
                        {errors.username}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <hr></hr>
              <h5 className="mb-2">
                <i className="ti ti-key text-primary me-1" />
                Login Information
              </h5>
              <div className="row">
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Email Address<span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.email && (
                    <span className="errors text-danger">{errors.email}</span>
                  )}
                </div>
                <div className="col-lg-6 col-sm-12">
                  <div className="mb-3">
                    <label className="form-label">
                      Password<span className="text-danger">*</span>
                    </label>
                    <div className="pass-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control"
                        placeholder="Password"
                        name="password"
                        autoComplete="password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="ti toggle-password ti-eye-off text-gray-9"
                      />
                    </div>
                    {errors.password && (
                      <span className="errors text-danger">
                        {errors.password}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Submit Details"
                    loadingText="Submitting..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddNew;
